// Layouts
import { createRouter, createWebHistory } from 'vue-router';

import authService from '@services/auth.service';

import store from '@store';
import { FETCH_COMPANIES_HARD, SET_SELECTED_COMPANY } from '@store/modules/company/constants';
import { SET_SELECTED_PLACE } from '@store/modules/company/place/constants';
import { useLoaderStore } from '@store/pinia-utils/loader';

import Default from '@layouts/Default.vue';
import Multicolumn from '@layouts/Multicolumn.vue';
import NotFound from '@layouts/NotFound.vue';

import activityLog from '@views/ActivityLog/routes';
import auditRoutes from '@views/Audits/routes';
// Routes
import authRoutes, { beforeEachGuard as authBeforeEachGuard } from '@views/Auth/routes';
import billingRoutes from '@views/Billing/routes';
import documentsRoutes from '@views/Documents/routes';
import overviewRoutes from '@views/Overview/routes';
import planRoutes from '@views/Plan/routes';
import settingsRoutes from '@views/Settings/routes';
import setup from '@views/SetUp/routes';
import team from '@views/Team/routes';
import traceabilityRoutes from '@views/TraceabilityModule/routes';

const routes = [
    ...authRoutes,
    {
        path: '/contact',
        component: Default,
        children: [
            {
                path: '',
                name: 'contact',
                components: {
                    base: () => import('@views/contact.vue'),
                },
            },
        ],
    },
    {
        path: '/bootstrap',
        component: Multicolumn,
        children: [
            {
                path: '',
                component: () => import('@views/bootstrap.vue'),
            },
        ],
    },
    {
        path: '/audits',
        component: Multicolumn,
        children: auditRoutes,
    },
    {
        path: '/set-up',
        component: Multicolumn,
        children: setup,
    },
    {
        path: '/activity-log',
        component: Multicolumn,
        children: activityLog,
    },
    {
        path: '/audits',
        component: Multicolumn,
        children: auditRoutes,
    },
    {
        path: '/plan',
        component: Multicolumn,
        children: planRoutes,
    },
    {
        path: '/traceability',
        component: Multicolumn,
        children: traceabilityRoutes,
    },
    {
        path: '/billing',
        component: Multicolumn,
        children: billingRoutes,
    },
    {
        path: '/team',
        component: Multicolumn,
        children: team,
    },
    {
        path: '/documents',
        component: Multicolumn,
        children: documentsRoutes,
    },
    {
        path: '/overview',
        component: Multicolumn,
        children: overviewRoutes,
    },
    {
        path: '',
        redirect: { name: 'overview' },
        component: Default,
        children: [...settingsRoutes],
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not_found',
        component: NotFound,
        meta: { noNav: true, newAccount: true },
    },
];

// It will just take access_token from the url once when script will be loaded
if (window.location.search.match(/access_token=/)) {
    const urlParams = new URLSearchParams(window.location?.search);
    const token = urlParams?.get('access_token') || null;
    authService?.forceSetToken(token);
}

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior() {
        // Scroll app-content div to top
        const appContent = document.getElementById('app-content');
        if (appContent) {
            appContent.scrollTop = 0;
        }
        return { left: 0, top: 0 };
    },
    routes,
    linkActiveClass: 'active',
});

// Will catch all useless error messages from VueRouter
/*
const originalPush = router.push;
router.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalPush.call(this, location, onResolve, onReject);
    }
    return originalPush.call(this, location).catch((err) => {
        if (router.isNavigationFailure(err)) {
            // resolve err
            return err;
        }
        // rethrow error
        return Promise.reject(err);
    });
};
*/

let timeout;
router.beforeEach((to, from, next) => {
    clearTimeout(timeout);

    // Fix to prevent infinity loader
    timeout = setTimeout(() => {
        useLoaderStore().setLoading(false);
    }, 2000);

    const _hsq = (window._hsq = window._hsq || []); // Hubspot
    _hsq.push(['setPath', to?.path]); // refresh hubspot page tracking
    _hsq.push(['trackPageView']); // restart tracking
    if (!store.getters['global/headerConfigured'] && to?.query?.hide_header === 'true') {
        store.commit('global/setShowHeader', false);
    }

    /**
     * If company is not exists and it's not signup page
     * Redirect user to the first company creation page
     */
    if (authService.isAuthorized && !store.getters.companies?.length && !to.name.includes('register')) {
        next({ name: 'register.company' });
        return;
    }

    if ((authService.isAuthorized || authService.isSharedLink) && to?.query?.place_id && to?.query?.company_id) {
        loadPlaceBeforeEachGuard(to.query.company_id, to.query.place_id);
    }
    authBeforeEachGuard(to, from, next);
});

router.afterEach(() => {
    clearTimeout(timeout);
});

export default router;

async function loadPlaceBeforeEachGuard(companyId, placeId) {
    await store.dispatch(FETCH_COMPANIES_HARD);
    await store.dispatch(SET_SELECTED_COMPANY, parseInt(companyId));
    await store.dispatch(SET_SELECTED_PLACE, parseInt(placeId));
}
